import React from 'react';
import { Typography, Card, CardContent, Box } from '@mui/material';
import HearingIcon from '@mui/icons-material/Hearing';
import BiotechIcon from '@mui/icons-material/Biotech';
import SpaIcon from '@mui/icons-material/Spa';

const services = [
  {
    title: 'Ohrakupunktur',
    icon: HearingIcon,
    color: '#e8f5e9',
    iconColor: '#2E7D32',
    description:
      'Gezielte Stimulation ausgewählter Akupunkturpunkte am Ohr zur Schmerzlinderung, Stressreduktion sowie begleitend bei Raucherentwöhnung und Gewichtsreduktion. Diese sanfte Methode unterstützt das Gleichgewicht des gesamten Organismus.',
    highlights: ['Schmerzlinderung', 'Stressreduktion', 'Raucherentwöhnung', 'Gewichtsreduktion'],
  },
  {
    title: 'Infusionen & Injektionen',
    icon: BiotechIcon,
    color: '#fff8e1',
    iconColor: '#D4A843',
    description:
      'Gezielte Zufuhr von Vitalstoffen wie hochdosiertem Vitamin C, Folsäure, B12 und weiteren Mikronährstoffen direkt in den Blutkreislauf. Dies ermöglicht eine optimale Bioverfügbarkeit und stärkt Immunsystem, Energie und Wohlbefinden spürbar.',
    highlights: ['Hochdosiertes Vitamin C', 'Folsäure & B12', 'Immunstärkung', 'Energieboost'],
  },
  {
    title: 'Reflexzonentherapie',
    icon: SpaIcon,
    color: '#fce4ec',
    iconColor: '#C62828',
    description:
      'Durch gezielte Massage der Reflexzonen an Händen und Füßen werden körpereigene Heilungsprozesse angeregt. Diese ganzheitliche Methode fördert die Durchblutung, löst Verspannungen und aktiviert die natürliche Selbstheilungskraft des Körpers.',
    highlights: ['Selbstheilungskraft', 'Durchblutungsförderung', 'Entspannung', 'Ganzheitlich'],
  },
];

function Leistungen() {
  return (
    <Box>
      {/* Header Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 60%, #388E3C 100%)',
          py: { xs: 7, md: 10 },
          px: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: -100, right: -100, width: 400, height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: 'italic',
            color: '#D4A843',
            fontSize: '1rem',
            letterSpacing: 2,
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          Naturheilkunde
        </Typography>
        <Typography
          variant="h3"
          sx={{
            color: '#fff',
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: '1.8rem', md: '2.5rem' },
          }}
        >
          Meine Leistungen
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', mt: 2, fontSize: { xs: '0.97rem', md: '1.05rem' } }}>
          Ganzheitliche Behandlungen für Ihr Wohlbefinden
        </Typography>
      </Box>

      {/* Services */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {services.map(({ title, icon: Icon, color, iconColor, description, highlights }) => (
            <Card
              key={title}
              sx={{
                borderRadius: '24px',
                border: '1px solid rgba(76,175,80,0.1)',
                overflow: 'hidden',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(46,125,50,0.14)',
                },
              }}
            >
              <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1B5E20, #D4A843, #4CAF50)' }} />
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      minWidth: 72,
                      width: 72,
                      height: 72,
                      borderRadius: '18px',
                      backgroundColor: color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 36, color: iconColor }} />
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontFamily: '"Playfair Display", serif', color: '#1B5E20', mb: 1.5 }}
                    >
                      {title}
                    </Typography>
                    <Typography sx={{ color: '#5C5244', lineHeight: 1.85, mb: 2.5, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                      {description}
                    </Typography>

                    {/* Highlight Tags */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {highlights.map((tag) => (
                        <Box
                          key={tag}
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            backgroundColor: 'rgba(46,125,50,0.08)',
                            border: '1px solid rgba(46,125,50,0.15)',
                            fontSize: '0.82rem',
                            color: '#2E7D32',
                            fontWeight: 700,
                            fontFamily: '"Lato", sans-serif',
                          }}
                        >
                          {tag}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Note */}
        <Box
          sx={{
            mt: 6,
            p: { xs: 3, md: 4 },
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #e8f5e9, #f0ede4)',
            border: '1px solid rgba(76,175,80,0.15)',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontStyle: 'italic',
              color: '#1B5E20',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.8,
            }}
          >
            „Jede Behandlung wird individuell auf Ihre Bedürfnisse abgestimmt.
            Sprechen Sie mich gerne an – ich berate Sie umfassend und persönlich."
          </Typography>
          <Typography sx={{ color: '#8D7A6B', mt: 1.5, fontSize: '0.9rem' }}>
            – Sylwia Spiller, Heilpraktikerin
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default Leistungen;
